/**
 * shareCard/build — a ShareCard from the app's own swim and set objects.
 *
 * Input is what the Swim tab already holds: the DerivedWorkout (its
 * `setGroups` ARE the sets, setGroups.ts, never a second set model) plus the
 * workout's uuid and start date, and the history the Scores tab reads. The
 * story line and every lineage number come from storyLine.ts, which runs the
 * tab's own code over the same facts; this file only reshapes.
 *
 * A ShareSetBlock is a run of consecutive same-stroke lengths inside a set:
 * the Rockalita's 8 lengths (Fly Back Breast Free Free Breast Back Fly) become
 * six blocks of 1, 1, 1, 2, 1, 1 × 25. Its `stroke` is the lap's effective
 * stroke, which IS a STROKE_COLORS key, so the renderer looks the color up
 * by it. Excluded lengths still count toward reps (a set is a structural
 * unit; the outlier first length of the Rockalita is still swum) but never
 * toward pace.
 *
 * Pure module.
 */
import type { DerivedWorkout } from "../lapAnalysis";
import type { Lap } from "../laps";
import type { SetGroup } from "../setGroups";
import { buildSetHrLineChart } from "../setHrLineChart";
import { hrSeriesInWindow, setHrStats, type HrPoint } from "../setHrWindow";
import { setHistoryRows, type SetHistoryRow } from "../setHistory";
import {
  factsForStory,
  lineageFor,
  setStoryLineFor,
  storyLineFor,
  type StoryClub,
  type StoryHistory,
} from "./storyLine";
import { MAX_HR_LENGTHS, SWIM_CARD_VERSION, type ShareSet, type ShareSetBlock, type ShareSetCard, type ShareSwimCard } from "./types";

/** The swim as the Swim tab holds it. */
export interface ShareSwimSource {
  uuid: string;
  /** ISO-8601 workout start (WorkoutSummary.startDate). */
  startDate: string;
  derived: DerivedWorkout;
}

/** Local calendar day "YYYY-MM-DD" of an ISO timestamp. */
function localIsoDay(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function roleFor(setId: string | null): ShareSetBlock["role"] {
  if (setId === "warmup" || setId === "cooldown") return setId;
  if (setId === null) return "other";
  return "main";
}

/** "2 × 25 Freestyle": the app's full stroke name in text (VP round 2, item 6;
 *  the ribbon's "Free" is a chip word, and the chips keep theirs). */
function blockLabel(reps: number, distanceM: number, stroke: string): string {
  return reps === 1 ? `${distanceM} ${stroke}` : `${reps} × ${distanceM} ${stroke}`;
}

/** Consecutive same-stroke lengths → one block each, in swim order. */
export function blocksFor(laps: Lap[], poolLengthMeters: number | null, role: ShareSetBlock["role"]): ShareSetBlock[] {
  const out: ShareSetBlock[] = [];
  let run: Lap[] = [];
  const flush = () => {
    if (run.length === 0) return;
    const stroke = run[0].strokeStyle;
    const distanceM = Math.round(run[0].lengthMeters ?? poolLengthMeters ?? 0);
    const counted = run.filter((l) => !l.effectiveExcluded && l.durationSeconds > 0);
    const block: ShareSetBlock = { label: blockLabel(run.length, distanceM, stroke), reps: run.length, distanceM, stroke, role };
    if (counted.length > 0 && distanceM > 0) {
      const meanS = counted.reduce((a, l) => a + l.durationSeconds, 0) / counted.length;
      block.paceSecPer100 = Math.round((meanS / distanceM) * 100);
    }
    out.push(block);
    run = [];
  };
  for (const lap of laps) {
    if (run.length > 0 && run[0].strokeStyle !== lap.strokeStyle) flush();
    run.push(lap);
  }
  flush();
  return out;
}

/** Best pace per 100 m from a lineage row, on the row's own basis. */
function bestPaceFrom(row: SetHistoryRow): number | undefined {
  if (row.basis === "setTime") {
    return row.fastest.meters > 0 ? Math.round((row.fastest.valueS / row.fastest.meters) * 100) : undefined;
  }
  return row.poolLengthMeters ? Math.round((row.fastest.valueS / row.poolLengthMeters) * 100) : undefined;
}

/** The mean BPM of the samples in [startMs, endMs), rounded; null with none. */
function meanBpm(series: readonly HrPoint[], startMs: number, endMs: number): number | null {
  const pts = hrSeriesInWindow(series, startMs, endMs);
  return pts.length === 0 ? null : Math.round(pts.reduce((n, p) => n + p.bpm, 0) / pts.length);
}

/**
 * The set's heart rate as SetDetailSheet shows it: setHrStats rounded, and one
 * BPM per length, buildSetHrLineChart's per-length average rounded (null where
 * a length holds no sample); `lengths` counts the readings. The chart needs 4
 * samples in the window, so a sparser set (June 11's warmup: 2 samples, one
 * length) averages each length directly, which is the same rule. `restAfter`
 * is the mean in the gap before `next`, the dip the swim card's line draws
 * between sets. No samples in the window, no `hr`.
 */
function setHr(group: SetGroup, series: readonly HrPoint[], next: SetGroup | undefined): ShareSet["hr"] {
  const stats = setHrStats(series, group.startMs, group.endMs);
  if (!stats) return undefined;
  const chart = buildSetHrLineChart(group.laps, series, group.startMs, group.endMs);
  const perLength = chart
    ? chart.lengths.map((l) => (l.bpm === null ? null : Math.round(l.bpm)))
    : group.laps.map((l) => meanBpm(series, l.startMs, l.endMs));
  const hr: NonNullable<ShareSet["hr"]> = { avg: Math.round(stats.avg), peak: Math.round(stats.peak), lengths: perLength.filter((b) => b !== null).length };
  // A set past the wire's cap carries no line rather than a card no reader accepts.
  if (perLength.length <= MAX_HR_LENGTHS) hr.perLength = perLength;
  hr.restAfter = next ? meanBpm(series, group.endMs, next.startMs) : null;
  return hr;
}

function shareSet(group: SetGroup, poolLengthMeters: number | null, row: SetHistoryRow | undefined, hrSeries: readonly HrPoint[], next?: SetGroup): ShareSet {
  const set: ShareSet = {
    name: group.label,
    blocks: blocksFor(group.laps, poolLengthMeters, roleFor(group.setId)),
    totalM: Math.round(group.meters),
  };
  if (row) {
    set.lineage = { timesSwum: row.timesSwum };
    const best = bestPaceFrom(row);
    if (best !== undefined) set.lineage.bestPaceSecPer100 = best;
  }
  const hr = setHr(group, hrSeries, next);
  if (hr) set.hr = hr;
  return set;
}

/**
 * The swim card. `history` is every swim the app has counted (this one may be
 * among them; it is excluded by uuid before "prior" is computed), `club` the
 * clubmates who swam this week for rule 6 and the club line.
 */
export function buildShareCardFromSwim(swim: ShareSwimSource, history: StoryHistory, club?: StoryClub, owner?: string): ShareSwimCard {
  const { derived } = swim;
  const facts = factsForStory(derived, swim.uuid, swim.startDate, history);
  const storyInputs = { uuid: swim.uuid, date: swim.startDate, distanceM: derived.totalDistanceMeters, facts, history, club };
  const lineage = lineageFor(storyInputs);

  const card: ShareSwimCard = {
    v: 1,
    kind: "swim",
    date: localIsoDay(swim.startDate),
    poolM: derived.poolLengthMeters ?? 0,
    distanceM: Math.round(derived.totalDistanceMeters),
    // FLOOR, not round: the Swim tab's hero runs fmtClockDuration on the raw
    // seconds and that function floors (format.ts), so 2950.6 s reads 49:10
    // there; a rounded 2951 read 49:11 on the card (VP round 2, item 5).
    durationSec: Math.floor(derived.totalDurationSeconds),
    sets: derived.setGroups.map((g, i) =>
      shareSet(g, derived.poolLengthMeters, g.setId === null ? undefined : lineage.get(g.setId)?.row, derived.hrSeries, derived.setGroups[i + 1]),
    ),
    story: storyLineFor(storyInputs),
  };
  // The swim's average on the set rows' own basis: the samples inside the set
  // windows, rests between sets excluded, so it cannot read lower than every
  // row (orchestrator ruling, 10:3x). No set windows, no rows to disagree
  // with: the whole-workout average.
  const inSets = derived.setGroups.flatMap((g) => hrSeriesInWindow(derived.hrSeries, g.startMs, g.endMs));
  const avgHr = derived.setGroups.length === 0 ? derived.avgHr : inSets.length > 0 ? inSets.reduce((n, p) => n + p.bpm, 0) / inSets.length : null;
  if (avgHr !== null && Number.isFinite(avgHr)) card.avgHr = Math.round(avgHr);
  if (club && club.swamThisWeek.length > 0) card.clubmates = [...club.swamThisWeek];
  // The swimmer's first name, and with it v2 (SWIM_CARD_VERSION). No name, v1.
  const first = owner?.trim().split(/\s+/)[0];
  if (first) {
    card.v = SWIM_CARD_VERSION;
    card.owner = first.slice(0, 24);
  }
  return card;
}

/**
 * The set card: one SetGroup as the hero, its lineage from the whole history
 * (the swim it came from included, if the app has counted it). `hrSeries` is
 * the swim's heart-rate series (DerivedWorkout.hrSeries); without it the set
 * carries no heart rate.
 */
export function buildShareCardFromSet(set: SetGroup, history: StoryHistory, owner?: string, hrSeries: readonly HrPoint[] = []): ShareSetCard {
  const pool = set.laps[0]?.lengthMeters ?? null;
  const row = set.setId === null ? undefined : setHistoryRows(history.facts).find((r) => r.setId === set.setId);
  const card: ShareSetCard = {
    v: 1,
    kind: "set",
    set: shareSet(set, pool, row, hrSeries),
    story: setStoryLineFor(set.setId, history),
  };
  if (owner) card.owner = owner;
  return card;
}
